import { addDoc, collection, doc, runTransaction, serverTimestamp, updateDoc } from "firebase/firestore"
import { useFirestore } from "vuefire"
import { useAuth } from "@/composables/useAuth"
import { useStorage } from "@/composables/useStorage"
import type {
  BaseTicketMessage,
  CreateTicketDto,
  CustomerSnapshot,
  MessagePreview,
  Order,
  PersonSnapshot,
  Ticket,
  TicketFileMessage,
  TicketImageMessage,
  TicketMessageUnion,
  TicketStatus,
  TicketTextMessage,
} from "@wowpress/types"
import {
  messageError,
  messageLoading,
  recentTicket,
  ticketError,
  ticketLoading,
  ticketMessages,
} from "@/services/subscriptionManager"
import { ordInfoToSnapshot } from "@/utils/mapper"

// --- 티켓 생성 시 필요한 데이터 타입 정의 ---
interface CreateWidgetTicketOptions {
  type: "preOrder" | "postOrder" | "serviceInquiry"
  initialMessage: string
  order?: Order
}

// URL 유효성 검사를 위한 간단한 함수 (필요에 따라 더 정교하게 만들 수 있음)
const isValidUrl = (string: string): boolean => {
  try {
    new URL(string)
    return string.startsWith("http://") || string.startsWith("https://")
  } catch (_) {
    return false
  }
}

// --- 컴포저블 함수 ---
// 이 함수는 호출될 때마다 전역 상태와 해당 상태를 조작하는 함수들에 접근할 수 있게 해줍니다.
export function useTicket() {
  // 컨텍스트 의존적인 부분은 함수 내부에 유지
  const { user } = useAuth()
  const db = useFirestore()
  const { uploadFile } = useStorage()

  const createTicket = async (options: CreateWidgetTicketOptions): Promise<string | null> => {
    if (!user.value || !db) {
      // user는 useAuth에서 가져온 ref
      ticketError.value = new Error("사용자 인증 정보 또는 DB 인스턴스가 없습니다.") // 전역 에러 상태 사용
      return null
    }
    ticketLoading.value = true // 전역 로딩 상태 사용
    ticketError.value = null

    try {
      let customer: CustomerSnapshot
      if (!user.value.isAnonymous && user.value.displayName) {
        customer = {
          id: user.value.uid,
          name: user.value.displayName,
          email: user.value.email || "",
          anonymous: user.value.isAnonymous ?? false,
          storeId: "wowpress",
        }
      } else {
        customer = {
          id: user.value.uid,
          name: "익명 사용자",
          anonymous: user.value.isAnonymous ?? false,
          storeId: "wowpress",
        }
      }

      const ticketData: CreateTicketDto = {
        customer: customer,
        title: `[${options.type}] ${options.initialMessage.substring(0, 30)}${options.initialMessage.length > 30 ? "..." : ""}`,
        description: options.initialMessage,
        priority: "medium",
        source: "web",
      }

      if (options.order) {
        ticketData.order = ordInfoToSnapshot(options.order)
      }

      const finalTicketData = {
        ...ticketData,
        status: "chatbot_handling" as TicketStatus,
        channel: "chatbotWidget",
        assignedTo: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        references: [],
      }

      const ticketsCollectionRef = collection(db, "tickets")
      const docRef = await addDoc(ticketsCollectionRef, finalTicketData)

      if (docRef.id && options.initialMessage) {
        // addMessage는 내부 함수이므로 직접 호출
        await addMessage(docRef.id, options.initialMessage)
      }

      console.log("[useTicket] Ticket created:", docRef.id)
      // 리스너가 recentTicket 업데이트
      // recentTicket은 subscriptionManager의 리스너가 자동으로 업데이트할 것임
      return docRef.id
    } catch (e: any) {
      console.error("[useTicket] Error creating ticket:", e)
      ticketError.value = e // 전역 에러 상태 사용
      return null
    } finally {
      ticketLoading.value = false // 전역 로딩 상태 사용
    }
  }

  const addMessage = async (
    ticketIdToAdd: string,
    content: string | File,
    metadata: Record<string, any> | undefined | null = null,
  ): Promise<boolean> => {
    console.log(`[useTicket] Adding message to ticket: ${ticketIdToAdd}`, content)
    if (!user.value || !db || !ticketIdToAdd || !content) {
      messageError.value = new Error("사용자 정보, DB, 티켓 ID, 메시지 내용이 모두 필요합니다.") // 전역 에러 상태 사용
      return false
    }
    console.log(`[useTicket] Adding message to ticket: ${ticketIdToAdd}`)
    messageLoading.value = true // 전역 로딩 상태 사용
    messageError.value = null

    const authorSnapshot: PersonSnapshot = {
      id: user.value.uid,
      type: "customer",
      name: user.value.displayName ?? "고객",
      anonymous: user.value.isAnonymous ?? false,
      storeId: "wowpress",
    }

    const ticketRef = doc(db, "tickets", ticketIdToAdd)
    const messagesRef = collection(ticketRef, "messages")
    const newMessageRef = doc(messagesRef)
    const tempMessageId = newMessageRef.id

    let messageData: Omit<BaseTicketMessage, "id">
    let messagePreviewText: string
    let tempMessage: TicketMessageUnion | null = null

    try {
      // 임시 메시지 생성 및 추가
      if (typeof content === "string") {
        messageData = {
          type: "text",
          text: content.trim(),
          author: authorSnapshot,
          createdAt: serverTimestamp() as any,
          ticketId: ticketIdToAdd,
          status: "sent", // 성공 시 sent로 설정,
          metadata: metadata ?? null,
        } as Omit<TicketTextMessage, "id">
        messagePreviewText = content.trim()

        // 임시 메시지 생성 (텍스트)
        tempMessage = {
          id: tempMessageId,
          type: "text",
          text: content.trim(),
          author: authorSnapshot,
          createdAt: { toDate: () => new Date() } as any, // 클라이언트 시간 사용
          ticketId: ticketIdToAdd,
          status: "sending",
          metadata: metadata ?? null,
        } as TicketTextMessage
      } else {
        const file = content
        messagePreviewText = file.name

        // 파일 메시지의 경우 먼저 임시 메시지 생성
        if (file.type.startsWith("image/")) {
          // 이미지 파일인 경우
          tempMessage = {
            id: tempMessageId,
            type: "image",
            uri: URL.createObjectURL(file), // 임시 URL 생성
            author: authorSnapshot,
            createdAt: { toDate: () => new Date() } as any,
            ticketId: ticketIdToAdd,
            status: "sending",
            metadata: metadata ?? null,
          } as TicketImageMessage
        } else {
          // 일반 파일인 경우
          tempMessage = {
            id: tempMessageId,
            type: "file",
            name: file.name,
            size: file.size,
            mimeType: file.type ?? null,
            uri: "#", // 임시 URI
            author: authorSnapshot,
            createdAt: { toDate: () => new Date() } as any,
            ticketId: ticketIdToAdd,
            status: "sending",
            metadata: metadata ?? null,
          } as TicketFileMessage
        }

        // 임시 메시지를 ticketMessages에 추가
        if (tempMessage) {
          ticketMessages.value = [...ticketMessages.value, tempMessage]
        }

        // 실제 파일 업로드 시도
        const filePath = `tickets/${ticketIdToAdd}/attachments/${Date.now()}_${file.name}`
        const downloadURL = await uploadFile(file, { path: filePath })

        if (!downloadURL) throw new Error("파일 업로드 실패")

        const commonFileData = {
          uri: downloadURL,
          author: authorSnapshot,
          createdAt: serverTimestamp() as any,
          ticketId: ticketIdToAdd,
          status: "sent", // 성공 시 sent로 설정,
          metadata: metadata,
        }

        if (file.type.startsWith("image/")) {
          messageData = { ...commonFileData, type: "image" } as Omit<TicketImageMessage, "id">
        } else {
          messageData = {
            ...commonFileData,
            type: "file",
            name: file.name,
            size: file.size,
            mimeType: file.type ?? null,
          } as Omit<TicketFileMessage, "id">
        }
      }

      // 텍스트 메시지인 경우 여기서 임시 메시지 추가
      if (typeof content === "string" && tempMessage) {
        ticketMessages.value = [...ticketMessages.value, tempMessage]
      }

      const messagePreview: MessagePreview = {
        id: tempMessageId,
        type: "text", // 동적으로 설정된 타입 사용
        text: messagePreviewText.substring(0, 100), // 텍스트 또는 파일 이름
        author: authorSnapshot,
        createdAt: serverTimestamp() as any,
      }

      // Firestore에 실제 메시지 저장
      await runTransaction(db, async (transaction) => {
        transaction.set(newMessageRef, messageData)
        transaction.update(ticketRef, { lastMessage: messagePreview, updatedAt: serverTimestamp() })
      })

      console.log("[useTicket] Message added successfully.")
      // 성공 시 임시 메시지 상태를 sent로 업데이트
      if (tempMessage) {
        ticketMessages.value = ticketMessages.value.map((msg) =>
          msg.id === tempMessageId ? { ...msg, status: "sent" } : msg,
        )
      }
      return true
    } catch (e: any) {
      console.error("[useTicket] Error adding message:", e)
      messageError.value = e // 전역 에러 상태 사용

      // 실패 시 임시 메시지 상태를 error로 업데이트
      if (tempMessage) {
        ticketMessages.value = ticketMessages.value.map((msg) =>
          msg.id === tempMessageId ? { ...msg, status: "error" } : msg,
        )
      }

      return false
    } finally {
      messageLoading.value = false // 전역 로딩 상태 사용
    }
  }

  const updateTicket = async (ticketIdToUpdate: string, updateData: Partial<Ticket>): Promise<boolean> => {
    try {
      if (!user.value || !db || !ticketIdToUpdate) {
        return false
      }
      console.log("[useTicket] Updating ticket:", ticketIdToUpdate, updateData)
      const ticketRef = doc(db, "tickets", ticketIdToUpdate)
      await updateDoc(ticketRef, updateData)

      return true
    } catch (e: any) {
      console.error("[useTicket] Error updating ticket:", e)
      return false
    }
  }

  const addBotMessage = async (ticketIdToAdd: string, content: string | File): Promise<boolean> => {
    if (!user.value || !db || !ticketIdToAdd || !content) {
      // content가 falsy이면 진행하지 않음
      messageError.value = new Error("사용자 정보, DB, 티켓 ID 또는 메시지 내용이 없습니다.")
      return false
    }
    console.log(`[useTicket] Adding bot message to ticket: ${ticketIdToAdd}`)
    messageLoading.value = true
    messageError.value = null

    try {
      const authorSnapshot: PersonSnapshot = {
        id: "wowpress-bot",
        type: "bot",
      }

      const ticketRef = doc(db, "tickets", ticketIdToAdd)
      const messagesRef = collection(ticketRef, "messages")
      const newMessageRef = doc(messagesRef)

      let messageData: Omit<BaseTicketMessage, "id">
      let messagePreviewText: string

      if (typeof content === "string") {
        if (isValidUrl(content)) {
          // content가 URL인 경우
          messageData = {
            type: "image", // URL은 이미지로 간주 (필요시 파일 타입으로 변경 가능)
            uri: content,
            author: authorSnapshot,
            createdAt: serverTimestamp() as any,
            ticketId: ticketIdToAdd,
            status: "sent",
          } as Omit<TicketImageMessage, "id">
          messagePreviewText = "이미지"
        } else {
          // content가 일반 텍스트인 경우
          messageData = {
            type: "text",
            text: content.trim(),
            author: authorSnapshot,
            createdAt: serverTimestamp() as any,
            ticketId: ticketIdToAdd,
            status: "sent",
          } as Omit<TicketTextMessage, "id">
          messagePreviewText = content.trim()
        }
      } else {
        // content is File
        const file = content
        const filePath = `tickets/${ticketIdToAdd}/attachments/${Date.now()}_${file.name}`
        const downloadURL = await uploadFile(file, { path: filePath })

        if (!downloadURL) throw new Error("봇 파일 업로드 실패")
        messagePreviewText = file.name

        const commonFileData = {
          uri: downloadURL,
          author: authorSnapshot,
          createdAt: serverTimestamp() as any,
          ticketId: ticketIdToAdd,
          status: "sent",
        }

        if (file.type.startsWith("image/")) {
          messageData = { ...commonFileData, type: "image" } as Omit<TicketImageMessage, "id">
        } else {
          messageData = {
            ...commonFileData,
            type: "file",
            name: file.name,
            size: file.size,
            mimeType: file.type || undefined,
          } as Omit<TicketFileMessage, "id">
        }
      }

      const messagePreview: MessagePreview = {
        id: newMessageRef.id,
        type: "text", // 동적으로 설정된 타입 사용
        text: messagePreviewText.substring(0, 100), // 텍스트, "이미지", 또는 파일 이름
        author: authorSnapshot,
        createdAt: serverTimestamp() as any,
      }

      await runTransaction(db, async (transaction) => {
        transaction.set(newMessageRef, messageData)
        transaction.update(ticketRef, { lastMessage: messagePreview, updatedAt: serverTimestamp() })
      })
      console.log("[useTicket] Bot message added successfully.")
      return true
    } catch (e: any) {
      console.error("[useTicket] Error adding bot message:", e)
      messageError.value = e
      return false
    } finally {
      messageLoading.value = false
    }
  }

  // --- 반환 값 (모듈 스코프 상태와 내부 함수) ---
  // --- 반환 값 (전역 상태와 내부 함수) ---
  return {
    // 상태 (전역 Ref from subscriptionManager)
    ticketLoading,
    ticketError,
    messageLoading,
    messageError,
    ticketMessages,
    recentTicket,

    // 함수 (컨텍스트 접근 가능)
    createTicket,
    addMessage,
    addBotMessage,
    updateTicket,
  }
}
